/**
 * Paywall exposure and intent (PIX-558).
 *
 * Two events, deliberately. Exposure is "this student saw that something was
 * locked"; intent is "this student tried to get into it". Reporting only
 * intent throws away the denominator, and reporting only exposure conflates
 * someone who scrolled past a lock icon with someone who wanted through it.
 * The ratio between them is the actual question: does the paywall convert
 * attention into demand, and for which modules?
 *
 * Both carry the module that was gated, so paywall demand can be joined to
 * what the student later bought - or didn't. That link is what makes "which
 * locked feature drives checkout" answerable.
 */
import { analytics } from './analytics.js';
import { EVENTS, PROPS } from './events.js';

/**
 * Modules already reported as seen this session.
 *
 * Sidebar locks re-render on every navigation, so without this a single
 * student browsing for ten minutes would look like dozens of paywall hits
 * and the exposure-to-intent ratio would be meaningless.
 */
const seenThisSession = new Set();

/**
 * Record that a locked module was rendered in front of the student.
 * Deduplicated per module for the life of the page session.
 */
export function trackPaywallExposure(module, source, properties = {}) {
  if (!module) return;
  const key = `${source}:${module}`;
  if (seenThisSession.has(key)) return;
  seenThisSession.add(key);

  analytics.track(EVENTS.PAYWALL_ENCOUNTERED, {
    [PROPS.PAYWALL_MODULE]: module,
    [PROPS.PAYWALL_SOURCE]: source,
    ...properties,
  });
}

/**
 * Record that the student actively tried to open locked content. Never
 * deduplicated - repeatedly bouncing off the same paywall is a real and
 * interesting signal, not noise.
 */
export function trackPaywallIntent(module, source, properties = {}) {
  if (!module) return;
  analytics.track(EVENTS.PAYWALL_INTENT, {
    [PROPS.PAYWALL_MODULE]: module,
    [PROPS.PAYWALL_SOURCE]: source,
    ...properties,
  });
}

/** Clear the exposure memo. Called on logout so the next user starts fresh. */
export function resetPaywallExposure() {
  seenThisSession.clear();
}
